import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Autocomplete,
  RadioGroup,
  FormControlLabel,
  Radio,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import themestyle from "../theme";
import {
  textFieldSX,
  FormLabel,
  autocompleteListboxSX,
} from "../utils/formField";
import { CLASS_BUTTONS, PASS_TYPE_OPTIONS } from "../utils/const";
import CustomSnackbar from "../components/Snackbar";
import { useMutation, useQuery } from "@apollo/client/react";
import { CREATE_PASS, UPDATE_PASS } from "../graphql/mutation";
import {
  GET_PASS_BY_ID,
  GET_INSTRUCTOR_LIST,
  GET_CLASS_OPTIONS,
} from "../graphql/query";
import { isClassSelectable } from "../utils/classHelpers";
import { useNoNumberScroll } from "../utils/useNoNumberScroll";
import type {
  CreatePassInput,
  CreatePassMutationResponse,
  UpdatePassMutationResponse,
  GetPassByIdResponse,
  PassFormMode,
} from "../interfaces/pass.interface";

type AddPassFormProps = {
  mode?: PassFormMode;
  id?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  onEdit?: () => void;
  onDelete?: (id: string, name: string) => void;
};

// Days represented by one unit of a time-based pass.
const DAYS_PER_UNIT: Record<string, number> = { Weekly: 7, Monthly: 30 };

// Pass types whose validity is defined by a number of weeks/months.
const isDurationBased = (passType: string) =>
  passType === "Weekly" || passType === "Monthly";

// Compute the pass validity (in days) from its type and the configured count.
const computeValidityDays = (passType: string, count: number) => {
  if (passType === "Drop-in") return 1;
  const per = DAYS_PER_UNIT[passType] || 0;
  return per * (count > 0 ? count : 1);
};

// Reverse: derive the weeks/months count from a stored validityDays value.
const deriveDurationCount = (passType: string, validityDays?: number) => {
  const per = DAYS_PER_UNIT[passType];
  if (!per || !validityDays || validityDays <= 0) return 1;
  return Math.max(1, Math.round(validityDays / per));
};

const AddPassForm: React.FC<AddPassFormProps> = ({
  mode = "add",
  id,
  onSuccess,
  onCancel,
  onEdit,
  onDelete,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Stop the mouse wheel / swipe from changing number fields; value should only
  // change via the up/down arrows or by typing.
  const noOfClassesRef = useNoNumberScroll<HTMLInputElement>();
  const durationCountRef = useNoNumberScroll<HTMLInputElement>();
  const passCostRef = useNoNumberScroll<HTMLInputElement>();

  const [formData, setFormData] = useState({
    passType: "",
    noOfClasses: 1,
    // durationCount = number of weeks (Weekly) or months (Monthly). Used to
    // compute validityDays on submit; not sent to the API directly.
    durationCount: 1,
    passName: "",
    passCost: "" as any,
    status: "Active",
    description: "",
    instructors: [] as string[],
    passId: "",
    classIds: [] as string[],
    classNames: [] as string[],
    styleType: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error">(
    "success",
  );

  const isViewMode = mode === "view";

  const { data: passData } = useQuery<GetPassByIdResponse, { id: string }>(
    GET_PASS_BY_ID,
    {
      variables: { id: id || "" },
      skip: mode === "add" || !id,
      fetchPolicy: "network-only",
    },
  );

  const { data: instructorData } = useQuery(GET_INSTRUCTOR_LIST, {
    variables: { page: 1, perPage: 100, search: "" },
    fetchPolicy: "network-only",
  });

  const instructorOptions =
    instructorData?.getInstructorList?.data?.map((i: any) => ({
      label: i.fullname,
      value: i.fullname,
    })) || [];

  const { data: classData, loading: classLoading } = useQuery(
    GET_CLASS_OPTIONS,
    {
      variables: { page: 1, perPage: 100, search: "" },
      // Always refetch the class list when the pass form opens so classes added
      // after this query was first cached still appear in the linked-class dropdown.
      // (CREATE_CLASS/UPDATE_CLASS don't invalidate this query, and cache-first
      // would otherwise serve a stale snapshot missing the new classes.)
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-and-network",
    },
  );

  const formatDate = (dateStr: string) => {
    if (!dateStr || dateStr === "N/A") return dateStr;
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year}`;
  };

  const classOptions = useMemo(
    () => {
      const selected = new Set(formData.classIds);
      return (classData?.getClassList?.data || [])
        // Only offer classes that still have a session today or later. Keep any class
        // already linked to this pass (edit mode) so it doesn't vanish from the selection.
        .filter((c: any) => isClassSelectable(c) || selected.has(c.id))
        .map((c: any) => {
          const dates = c.scheduledDates || [];
          const startDate = dates.length > 0 ? formatDate(dates[0]) : "N/A";
          const endDate =
            dates.length > 0 ? formatDate(dates[dates.length - 1]) : "N/A";
          return {
            label: c.className,
            value: c.id,
            instructorName: c.instructorName,
            totalNoOfClasses: c.totalNoOfClasses,
            classType: c.classType,
            startDate,
            endDate,
          };
        });
    },
    [classData, formData.classIds],
  );

  const [createPass, { loading: createLoading }] = useMutation<
    CreatePassMutationResponse,
    { input: CreatePassInput }
  >(CREATE_PASS);

  const [updatePass, { loading: updateLoading }] = useMutation<
    UpdatePassMutationResponse,
    { id: string; input: any }
  >(UPDATE_PASS);

  const loading = createLoading || updateLoading;

  useEffect(() => {
    if (passData?.getPassById?.success && passData.getPassById.data) {
      const data = passData.getPassById.data;
      setFormData({
        passType: data.passType || "",
        noOfClasses: data.noOfClasses || 1,
        durationCount: deriveDurationCount(data.passType || "", data.validityDays),
        passName: data.passName || "",
        passCost: data.passCost || "",
        status: data.status || "Active",
        description: data.description || "",
        instructors: data.instructors || [],
        passId: data.passId || "",
        classIds:
          data.classIds && data.classIds.length
            ? data.classIds
            : data.classId
            ? [data.classId]
            : [],
        classNames:
          data.classNames && data.classNames.length
            ? data.classNames
            : data.className
            ? [data.className]
            : [],
        styleType: data.styleType || "",
      });
    }
  }, [passData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let val: any = value;
    if (name === "noOfClasses") val = value === "" ? "" : parseInt(value);
    if (name === "durationCount") val = value === "" ? "" : parseInt(value);
    if (name === "passCost") val = value === "" ? "" : parseFloat(value);
    setFormData((prev) => ({ ...prev, [name]: val }));
    const error = validateField(name, val);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validateField = (name: string, value: any) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    if (name === "passType" && !trimmed) return "Pass type is required";
    if (name === "noOfClasses" && (trimmed === "" || trimmed <= 0))
      return "Number of classes must be greater than 0";
    if (name === "durationCount" && (trimmed === "" || trimmed <= 0))
      return "Duration must be greater than 0";
    if (name === "passName") {
      if (!trimmed) return "Pass name is required";
      if (!/^(?=.*[A-Za-z])[A-Za-z0-9 &_-]+$/.test(trimmed))
        return "Invalid Pass Name";
      return "";
    }
    if (name === "passCost" && (trimmed === "" || trimmed < 0))
      return "Pass cost is required and cannot be negative";
    if (name === "classIds" && (!Array.isArray(value) || value.length === 0))
      return "At least one linked class is required";
    if (name === "instructors" && (!Array.isArray(value) || value.length === 0))
      return "At least one instructor is required";
    return "";
  };

  const handleSubmit = async () => {
    const fieldErrors: Record<string, string> = {};
    Object.entries(formData).forEach(([key, value]) => {
      if (
        key === "passId" ||
        key === "status" ||
        // durationCount only matters for Weekly/Monthly passes
        (key === "durationCount" && !isDurationBased(formData.passType))
      )
        return;
      const message = validateField(key, value);
      if (message) fieldErrors[key] = message;
    });

    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }

    // durationCount is a UI-only field; translate it into validityDays which
    // the API understands, and never send durationCount itself.
    const validityDays = computeValidityDays(
      formData.passType,
      formData.durationCount,
    );
    const { passId, durationCount, ...rest } = formData;
    const input =
      mode === "add"
        ? { ...rest, validityDays }
        : { ...rest, validityDays, passId };

    try {
      if (mode === "edit" && id) {
        const { data } = await updatePass({ variables: { id, input } });
        if (!data?.updatePass?.success) {
          setSnackbarMessage(
            data?.updatePass?.message || "Failed to update pass",
          );
          setSnackbarType("error");
          setSnackbarOpen(true);
          return;
        }
        setSnackbarMessage(data.updatePass.message || "Pass updated");
        setSnackbarType("success");
      } else {
        const { data } = await createPass({ variables: { input } });
        if (!data?.createPass?.success) {
          setSnackbarMessage(
            data?.createPass?.message || "Failed to create pass",
          );
          setSnackbarType("error");
          setSnackbarOpen(true);
          return;
        }
        setSnackbarMessage(data.createPass.message || "Pass created");
        setSnackbarType("success");
      }
      setSnackbarOpen(true);
      if (onSuccess) {
        setTimeout(onSuccess, 900);
      }
    } catch (err: any) {
      console.error(err);
    }
  };

  const isFormValid = () => {
    const requiredFieldsFilled =
      formData.passType.trim() !== "" &&
      formData.passName.trim() !== "" &&
      formData.passCost !== "" &&
      formData.noOfClasses > 0 &&
      formData.classIds.length > 0 &&
      formData.instructors.length > 0 &&
      (!isDurationBased(formData.passType) || formData.durationCount > 0);

    const noErrors = Object.values(errors).every((error) => !error);
    return requiredFieldsFilled && noErrors;
  };

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Box
        sx={{
          backgroundColor: themestyle.colors.white,
          borderRadius: "20px",
          border: "1px solid #E7EEDC",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            backgroundColor: themestyle.colors.lightGreenBg,
            px: 3,
            py: 2.5,
          }}
        >
          <Typography
            fontSize="20px"
            fontWeight={700}
            color={themestyle.colors.activeGreen}
            fontFamily={themestyle.fontFamily.Roboto}
          >
            Pass Information
          </Typography>
        </Box>

        <Box
          p={3}
          display="flex"
          flexDirection="column"
          gap={3}
          sx={{ px: { xs: 2, sm: 3, md: 5 } }}
        >
          <Box
            display="flex"
            gap={3}
            flexDirection={{ xs: "column", md: "row" }}
          >
            <Box flex={1}>
              <FormLabel>
                Pass Name{" "}
                <span style={{ color: themestyle.colors.error }}>*</span>
              </FormLabel>
              <TextField
                fullWidth
                sx={textFieldSX}
                name="passName"
                value={formData.passName}
                placeholder="eg. Summer Special 10-Pack"
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.passName}
                helperText={errors.passName}
                disabled={isViewMode}
              />
            </Box>
          </Box>
          <Box
            display="flex"
            gap={3}
            flexDirection={{ xs: "column", md: "row" }}
          >
            <Box flex={1}>
              <FormLabel>
                Linked Class{" "}
                <span style={{ color: themestyle.colors.error }}>*</span>
              </FormLabel>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={classOptions}
                loading={classLoading}
                disabled={isViewMode}
                isOptionEqualToValue={(opt: any, val: any) =>
                  opt.value === val.value
                }
                value={classOptions.filter((opt: any) =>
                  formData.classIds.includes(opt.value),
                )}
                onChange={(_e, newValues: any[]) => {
                  const classIds = newValues.map((v: any) => v.value);
                  const classNames = newValues.map((v: any) => v.label);
                  // Auto-fill instructors from the selected classes (union).
                  const instructors = Array.from(
                    new Set(
                      newValues
                        .map((v: any) => v.instructorName)
                        .filter(Boolean),
                    ),
                  ) as string[];
                  // Derive style type from the first selected class.
                  const first = newValues[0];
                  const styleType = first?.classType
                    ? first.classType === "STYLE_BASED"
                      ? "Style Based"
                      : first.classType === "KRUMP"
                      ? "Krump"
                      : "Choreography"
                    : "";
                  setFormData((prev) => ({
                    ...prev,
                    classIds,
                    classNames,
                    styleType,
                    // NOTE: passName is intentionally NOT modified here.
                    instructors:
                      instructors.length > 0 ? instructors : prev.instructors,
                  }));
                  const classError = validateField("classIds", classIds);
                  const instructorError = validateField(
                    "instructors",
                    instructors.length > 0 ? instructors : formData.instructors,
                  );
                  setErrors((prevErrors) => ({
                    ...prevErrors,
                    classIds: classError,
                    instructors: instructorError,
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search Class..."
                    sx={textFieldSX}
                    error={!!errors.classIds}
                    helperText={errors.classIds}
                  />
                )}
                slotProps={{
                  listbox: { sx: autocompleteListboxSX },
                }}
                renderOption={(props, option: any) => (
                  <li {...props}>
                    <Box
                      display="flex"
                      flexDirection={isMobile ? "column" : "row"}
                      justifyContent={isMobile ? "flex-start" : "space-between"}
                      alignItems={isMobile ? "flex-start" : "center"}
                      width="100%"
                      py={0.5}
                      gap={isMobile ? 1 : 0}
                    >
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Typography fontSize="14px" fontWeight={600}>
                          {option.label}
                        </Typography>
                        <Typography fontSize="12px" color="#666">
                          Instructor: <b>{option.instructorName}</b>
                        </Typography>
                      </Box>

                      <Box
                        display="flex"
                        gap={isMobile ? 1 : 3}
                        alignItems={isMobile ? "flex-start" : "center"}
                        flexDirection={isMobile ? "row" : "row"}
                        flexWrap="wrap"
                      >
                        <Typography fontSize="12px" color="#666">
                          Dates: <b>{option.startDate} to {option.endDate}</b>
                        </Typography>
                        <Typography fontSize="12px" color="#666">
                          Total Classes: <b>{option.totalNoOfClasses}</b>
                        </Typography>
                      </Box>
                    </Box>
                  </li>
                )}
              />
              <Typography
                variant="caption"
                sx={{ color: "#666", mt: 0.5, display: "block" }}
              >
                You can link multiple classes to this pass. Selecting classes
                auto-fills the Instructors.
              </Typography>
            </Box>
          </Box>
          <Box
            display="flex"
            gap={3}
            flexDirection={{ xs: "column", md: "row" }}
          >
            <Box flex={1}>
              <FormLabel>
                Pass Type{" "}
                <span style={{ color: themestyle.colors.error }}>*</span>
              </FormLabel>
              <Autocomplete
                options={PASS_TYPE_OPTIONS}
                disabled={isViewMode}
                value={
                  PASS_TYPE_OPTIONS.find(
                    (opt) => opt.value === formData.passType,
                  ) || null
                }
                onChange={(_, value) => {
                  const val = value?.value || "";
                  setFormData((prev) => ({
                    ...prev,
                    passType: val,
                    // reset to a sensible default when switching pass type
                    durationCount: isDurationBased(val) ? prev.durationCount || 1 : 1,
                  }));
                  const error = validateField("passType", val);
                  setErrors((prev) => ({
                    ...prev,
                    passType: error,
                    // duration is irrelevant for non-weekly/monthly types
                    durationCount: isDurationBased(val) ? prev.durationCount : "",
                  }));
                }}
                slotProps={{
                  listbox: { sx: autocompleteListboxSX },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Pass Type"
                    sx={textFieldSX}
                    error={!!errors.passType}
                    helperText={errors.passType}
                  />
                )}
              />
            </Box>
            <Box flex={1}>
              <FormLabel>
                No.of.Classes{" "}
                <span style={{ color: themestyle.colors.error }}>*</span>
              </FormLabel>
              <TextField
                fullWidth
                type="number"
                inputRef={noOfClassesRef}
                sx={textFieldSX}
                name="noOfClasses"
                value={formData.noOfClasses}
                placeholder="1"
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.noOfClasses}
                helperText={
                  errors.noOfClasses || "(Enter manually)"
                }
                disabled={isViewMode}
                FormHelperTextProps={{
                  sx: {
                    color: "#666",
                    fontSize: "12px",
                  },
                }}
              />
            </Box>
          </Box>

          {isDurationBased(formData.passType) && (
            <Box
              display="flex"
              gap={3}
              flexDirection={{ xs: "column", md: "row" }}
            >
              <Box flex={1}>
                <FormLabel>
                  {formData.passType === "Weekly"
                    ? "Number of Weeks"
                    : "Number of Months"}{" "}
                  <span style={{ color: themestyle.colors.error }}>*</span>
                </FormLabel>
                <TextField
                  fullWidth
                  type="number"
                  inputRef={durationCountRef}
                  sx={textFieldSX}
                  name="durationCount"
                  value={formData.durationCount}
                  placeholder="1"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.durationCount}
                  helperText={
                    errors.durationCount ||
                    `Valid for ${computeValidityDays(
                      formData.passType,
                      formData.durationCount,
                    )} days`
                  }
                  disabled={isViewMode}
                  FormHelperTextProps={{
                    sx: { color: "#666", fontSize: "12px" },
                  }}
                />
              </Box>
              <Box flex={1} />
            </Box>
          )}

          <Box
            display="flex"
            gap={3}
            flexDirection={{ xs: "column", md: "row" }}
          >
            <Box flex={1}>
              <FormLabel>
                Pass Cost{" "}
                <span style={{ color: themestyle.colors.error }}>*</span>
              </FormLabel>
              <TextField
                fullWidth
                type="number"
                inputRef={passCostRef}
                sx={textFieldSX}
                name="passCost"
                value={formData.passCost}
                placeholder="₹ 0.00"
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.passCost}
                helperText={errors.passCost}
                disabled={isViewMode}
              />
            </Box>
            <Box flex={1}>
              <FormLabel>
                Status <span style={{ color: themestyle.colors.error }}>*</span>
              </FormLabel>
              <RadioGroup
                row
                name="status"
                value={formData.status}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, status: e.target.value }))
                }
                sx={{ mt: 1 }}
              >
                <FormControlLabel
                  value="Active"
                  control={
                    <Radio
                      sx={{
                        color: "#D1D5DB",
                        "&.Mui-checked": {
                          color: themestyle.colors.activeGreen,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: { xs: "14px", sm: "16px" },
                      }}
                    >
                      Active
                    </Typography>
                  }
                  disabled={isViewMode}
                />
                <FormControlLabel
                  value="Inactive"
                  control={
                    <Radio
                      sx={{
                        color: "#D1D5DB",
                        "&.Mui-checked": {
                          color: themestyle.colors.activeGreen,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: { xs: "14px", sm: "16px" },
                      }}
                    >
                      Inactive
                    </Typography>
                  }
                  disabled={isViewMode}
                />
              </RadioGroup>
            </Box>
          </Box>

          <Box
            display="flex"
            gap={3}
            flexDirection={{ xs: "column", md: "row" }}
          >
            <Box flex={1}>
              <FormLabel>Description</FormLabel>
              <TextField
                fullWidth
                multiline
                rows={4}
                sx={textFieldSX}
                name="description"
                value={formData.description}
                placeholder="Enter details about what this pass includes..."
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.description}
                helperText={errors.description}
                disabled={isViewMode}
              />
            </Box>
          </Box>
          <Box
            display="flex"
            gap={3}
            flexDirection={{ xs: "column", md: "row" }}
          >
            <Box flex={1}>
              <FormLabel>
                Instructors{" "}
                <span style={{ color: themestyle.colors.error }}>*</span>
              </FormLabel>
              <Autocomplete
                multiple
                options={instructorOptions}
                disabled={isViewMode}
                value={instructorOptions.filter((opt: any) =>
                  formData.instructors.includes(opt.value),
                )}
                onChange={(_, values) => {
                  const instructorNames = values.map((v: any) => v.value);
                  setFormData((prev) => ({
                    ...prev,
                    instructors: instructorNames,
                  }));
                  const instructorError = validateField("instructors", instructorNames);
                  setErrors((prevErrors) => ({ ...prevErrors, instructors: instructorError }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={textFieldSX}
                    placeholder="Select Instructors"
                    error={!!errors.instructors}
                    helperText={errors.instructors}
                  />
                )}
              />
            </Box>
          </Box>
        </Box>

        <Box
          display="flex"
          justifyContent="flex-end"
          gap={isMobile ? 1 : 2}
          p={isMobile ? 1.5 : 3}
          sx={{ pt: 0, mb: isMobile ? 3 : 5, pr: { xs: 2, md: 5 } }}
        >
          <Button
            variant="outlined"
            onClick={() => onCancel && onCancel()}
            sx={{
              borderColor: themestyle.colors.buttonGreen,
              color: themestyle.colors.black,
              textTransform: "none",
              fontWeight: 600,
              px: isMobile ? 1.5 : 2,
              borderRadius: "8px",
              height: isMobile ? "36px" : "44px",
              fontSize: isMobile ? "13px" : "16px",
              minWidth: isMobile ? "70px" : "100px",
            }}
          >
            {mode === "view" ? "Cancel" : CLASS_BUTTONS.CANCEL}
          </Button>
          {isViewMode && onEdit ? (
            <Button
              variant="contained"
              onClick={onEdit}
              sx={{
                backgroundColor: themestyle.colors.buttonGreen,
                textTransform: "none",
                fontWeight: 600,
                whiteSpace: "nowrap",
                px: isMobile ? 1.5 : 5,
                height: isMobile ? "36px" : "44px",
                fontSize: isMobile ? "13px" : "16px",
                minWidth: isMobile ? "90px" : "110px",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: themestyle.colors.buttonGreenHover,
                },
              }}
            >
              {CLASS_BUTTONS.EDIT}
            </Button>
          ) : (
            !isViewMode && (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading || !isFormValid()}
                sx={{
                  backgroundColor: themestyle.colors.buttonGreen,
                  textTransform: "none",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  px: isMobile ? 1.5 : 5,
                  height: isMobile ? "36px" : "44px",
                  fontSize: isMobile ? "13px" : "16px",
                  minWidth: isMobile ? "75px" : "110px",
                  borderRadius: "8px",
                  "&:hover": {
                    backgroundColor: themestyle.colors.buttonGreenHover,
                  },
                }}
              >
                {loading ? "Saving" : mode === "add" ? "Add" : "Update"}
              </Button>
            )
          )}
        </Box>
      </Box>
      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarType}
        onClose={() => setSnackbarOpen(false)}
      />
    </Box>
  );
};
export default AddPassForm;
